import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, Tag, Truck, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart, lineDiscountPercent } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Seo } from "../components/Seo";

export default function CartPage() {
  const { items, updateQuantity, removeItem, subtotal, discountAmount, shipping, total, amountToFreeShip, freeShipThreshold, freeShipEnabled, tiers } = useCart();
  const { user } = useAuth();
  const [rewards, setRewards] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setRewards({ enabled: !!data.rewards_enabled, rate: data.rewards_redeem_rate || 100 }))
      .catch(() => {});
  }, []);

  const pointsValue = rewards?.enabled && (user?.points || 0) > 0
    ? +(user.points / rewards.rate).toFixed(2)
    : 0;

  if (items.length === 0) {
    return (
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-32 text-center">
        <ShoppingBag className="w-12 h-12 text-[#8C7B6B] mx-auto mb-6" />
        <h1 className="font-display text-xl sm:text-2xl uppercase tracking-wide text-[#1A1A1A]" data-testid="empty-cart-message">Your cart is empty</h1>
        <Link to="/shop" data-testid="continue-shopping-link" className="inline-flex items-center gap-2 mt-8 bg-[#8C7B6B] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-8 py-4 rounded-none hover:bg-[#75665A] transition-colors">
          Continue Shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Your Cart" path="/cart" noindex />
      <h1 className="font-display text-lg uppercase tracking-wide sm:text-3xl uppercase tracking-wide text-[#1A1A1A] mb-12">Your Cart</h1>

      <div className="space-y-6" data-testid="cart-items-list">
        {items.map((item) => {
          const pct = lineDiscountPercent(item, tiers);
          const lineTotal = item.price * item.quantity;
          const lineAfter = lineTotal * (1 - pct / 100);
          return (
            <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="flex gap-4 sm:gap-5 border-b border-[#DDD7CF] pb-6">
              <Link to={`/product/${item.product_id}`} className="w-20 h-20 bg-white rounded-lg overflow-hidden shrink-0">
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-product text-sm uppercase tracking-wide text-[#1A1A1A]" data-testid={`cart-item-name-${item.product_id}`}>
                      {item.name} <span className="normal-case text-[#6E675F] text-xs whitespace-nowrap">{item.dosage}</span>
                    </h3>
                    <p className="text-sm text-[#6E675F] mt-0.5">${item.price.toFixed(2)} CAD each</p>
                    {pct > 0 && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-[#2D6A4F] bg-[#EAF2EC] border border-[#2D6A4F]/20 rounded-none px-2 py-0.5" data-testid={`cart-line-discount-${item.product_id}`}>
                        <Tag className="w-3 h-3" /> {pct}% bulk discount
                      </span>
                    )}
                  </div>
                  <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="w-10 h-10 -mr-2 -mt-1 flex items-center justify-center rounded-full text-[#6E675F] hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" aria-label="Remove item">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-4 mt-3">
                  <div className="flex items-center border border-[#DDD7CF] rounded-none bg-white">
                    <button data-testid={`cart-qty-decrease-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="w-10 h-10 flex items-center justify-center hover:text-[#8C7B6B] transition-colors" aria-label="Decrease">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                    <button data-testid={`cart-qty-increase-${item.product_id}`} onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="w-10 h-10 flex items-center justify-center hover:text-[#8C7B6B] transition-colors" aria-label="Increase">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-right" data-testid={`cart-line-total-${item.product_id}`}>
                    {pct > 0 ? (
                      <>
                        <p className="text-xs text-[#6E675F] line-through">${lineTotal.toFixed(2)}</p>
                        <p className="font-medium text-[#2D6A4F]">${lineAfter.toFixed(2)}</p>
                      </>
                    ) : (
                      <p className="font-medium text-[#1A1A1A]">${lineTotal.toFixed(2)}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {amountToFreeShip > 0 && (
        <div className="mt-8 flex items-center gap-3 bg-[#F6F1E7] border border-[#D4B36A]/50 rounded-lg px-5 py-3" data-testid="free-shipping-nudge">
          <Truck className="w-4 h-4 text-[#8C7B6B] shrink-0" />
          <p className="text-sm text-[#1A1A1A]">
            Add <strong>${amountToFreeShip.toFixed(2)}</strong> more for <strong>FREE shipping</strong> (orders of ${freeShipThreshold}+ CAD ship free).
          </p>
        </div>
      )}
      {freeShipEnabled && amountToFreeShip <= 0 && (
        <div className="mt-8 flex items-center gap-3 bg-[#EAF2EC] border border-[#2D6A4F]/30 rounded-lg px-5 py-3" data-testid="free-shipping-unlocked">
          <Truck className="w-4 h-4 text-[#2D6A4F] shrink-0" />
          <p className="text-sm text-[#2D6A4F] font-medium">You've unlocked FREE shipping on this order.</p>
        </div>
      )}

      {pointsValue > 0 && (
        <div className="mt-3 flex items-center gap-3 bg-[#F6F1E7] border border-[#D4B36A]/50 rounded-lg px-5 py-3" data-testid="cart-points-nudge">
          <Gift className="w-4 h-4 text-[#8A6D2F] shrink-0" />
          <p className="text-sm text-[#1A1A1A]">
            You have <strong>{user.points.toLocaleString()} reward points</strong> — worth{" "}
            <strong className="text-[#8A6D2F]">${pointsValue.toFixed(2)} off</strong> this order. Redeem them at checkout.
          </p>
        </div>
      )}

      <div className="mt-8 sm:ml-auto sm:max-w-sm">
        <div className="space-y-2 border-t border-[#DDD7CF] pt-5">
          <div className="flex justify-between text-sm">
            <span className="text-[#6E675F]">Subtotal</span>
            <span className="text-[#1A1A1A]" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm" data-testid="cart-discount-row">
              <span className="text-[#2D6A4F] font-medium">Bulk discounts</span>
              <span className="text-[#2D6A4F] font-medium" data-testid="cart-discount-amount">−${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm" data-testid="cart-shipping-row">
            <span className="text-[#6E675F]">Shipping</span>
            {shipping > 0 ? (
              <span className="text-[#1A1A1A]" data-testid="cart-shipping-amount">${shipping.toFixed(2)}</span>
            ) : (
              <span className="text-[#2D6A4F] font-medium" data-testid="cart-shipping-amount">FREE</span>
            )}
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-[#DDD7CF]">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F]">Total</span>
            <span className="text-2xl sm:text-3xl font-medium text-[#1A1A1A]" data-testid="cart-total">${total.toFixed(2)} <span className="text-sm text-[#6E675F]">CAD</span></span>
          </div>
        </div>
        <button
          data-testid="proceed-to-checkout-button"
          onClick={() => navigate("/checkout")}
          className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-10 py-4 rounded-none hover:bg-[#75665A] hover:-translate-y-0.5 transition-transform duration-300"
        >
          Proceed to Checkout <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
